import React, { ChangeEvent, useCallback, useEffect, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	DrawerBody,
	DrawerFooter,
	Flex,
	FormControl,
	FormErrorMessage,
	FormLabel,
	Input,
	Link,
	Switch,
	Text,
	Tooltip,
	useDisclosure,
} from "@chakra-ui/react";
import { useToast } from "@/components/ui/Toast";
import { UNEXPECTED_ERROR } from "@/utils/envUtils";
import { ResourceType, SelectedFocusResources, SelectedResource } from "../FocusResourcePicker/SelectedFocusResources";
import { FocusResourcePickerInput } from "../FocusResourcePicker/FocusResourcePickerInput";
import { FocusDTO, FocusValidationConflictError, createOrUpdateFocus, deleteSavedFocusFromLibrary } from "@/js/api/focus";
import { colours } from "@/utils/themeUtils";
import { LibraryDrawerView } from "..";
import { ConfirmDeleteFocusModal } from "../ConfirmDeleteFocusModal";
import { transformSelectedResourcesToMatchAPIContract, transformUpsertResonseToFocusItem } from "../utils";
import { useSelectedClassroomStore } from "@/stores/v2/selectedClassroomStore";
import { useFocusStore } from "@/stores/v2/focusStore";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { envConfig } from "@/config";
import { markAllInvalidResources } from "./utils";
import { Notice } from "@/components/Notice";
import { useFeatureFlagsQuery } from "@/query/featureFlagsQuery";
import { FeatureFlag } from "@/api/classroomFeatureFlags";
import { ChakraFAIcon } from "@/components/ui/Icon";
import { isLockedFocusCompatibleUrl } from "@/js/utils/urlUtils";
import { captureFocusEvent } from "@/utils/posthogUtils";

export interface EditingFocus {
	id: string;
	name: string;
	active: boolean;
	resources: SelectedResource[];
	isOnTheFly: boolean;
	/**
	 * Whether or not the focus has "Locked Focus" enabled. Omitted for library items, only included for applied focuses.
	 */
	tabsLocked?: boolean;
	/**
	 * The students this focus is applied to.
	 */
	studentIds: string[];
}

export interface Props {
	closeDrawer: () => void;
	/**
	 * The selected focus that is either being edited and saved or edited and applied
	 */
	focusBeingEdited?: EditingFocus;
	/**
	 * Used to set the focus of the editing focus in the parent component. This is used when creating a focus so the parent component can pass a focus to be applied to the select students screen.
	 */
	setEditingFocus: (focus: EditingFocus) => void;
	/**
	 * `classIsActive` is used to determine whether the Apply button should say "Apply Focus" or "Preset Focus" when opened from within the context of a classroom.
	 * TODO: Check if class is Active from selectedClassroomStore state instead of prop drilling.
	 */
	classIsActive?: boolean;
	bubbleAppliedOrUpdatedFocus?: (appliedFocus: FocusDTO) => void;
	jumpToView: (view: LibraryDrawerView) => void;
	openedFromAppliedFocusPill?: boolean;
	/**
	 * This can be preset by the parent for the case when the teacher is kicked back to this page from the student selection page
	 */
	validationError?: FocusValidationConflictError;
	handleUpsertFailure: (error: Error) => void;
}

export function FocusPage(props: Props): JSX.Element {
	const MAX_FOCUS_NAME_LENGTH = envConfig.MAX_FOCUS_NAME_LENGTH;
	const MAX_FOCUSES_IN_LIBRARY = envConfig.MAX_FOCUSES_IN_LIBRARY;

	const confirmationModal = useDisclosure();
	const [isDeleting, setIsDeleting] = useState<boolean>(false);
	const [isUpserting, setIsUpserting] = useState<boolean>(false);

	const userSessionStore = useUserSessionStore();
	const focusStore = useFocusStore();
	const selectedClassroomStore = useSelectedClassroomStore();

	const featureFlagsQuery = useFeatureFlagsQuery(userSessionStore.active_device as string);

	const { successToast, errorToast } = useToast();

	const [saveFocusToLibrary, setSaveFocusToLibrary] = useState<boolean>(false);
	const [focusName, setFocusName] = useState<string>("");
	const [nameIsDuplicate, setNameIsDuplicate] = useState<boolean>(false);

	const [lockedFocus, setLockedFocus] = useState<boolean>(false);

	const [selectedResources, setSelectedResources] = useState<SelectedResource[]>([]);

	const openedFromClassroomPage = !!selectedClassroomStore.stableIdV1;

	// Pre-fill the selected resources from provided focus
	useEffect(() => {
		if (props.focusBeingEdited) {
			setSelectedResources((prev) => {
				return prev.length === 0 ? (props.focusBeingEdited?.resources as SelectedResource[]) : prev;
			});
		}

		if (props.focusBeingEdited?.tabsLocked) {
			setLockedFocus(true);
		}

		if (props.validationError) {
			setSelectedResources((prev) => markAllInvalidResources(prev, props.validationError as FocusValidationConflictError));
		}
	}, [props.focusBeingEdited, props.validationError, focusName]);

	// Pre-fill the name field from provided focus
	useEffect(() => {
		if (props.focusBeingEdited) {
			setFocusName((prev) => {
				return prev === "" ? props.focusBeingEdited?.name || "" : prev;
			});
		}

		if (props.validationError) {
			setNameIsDuplicate(!!props.validationError.duplicatedName);
		}
	}, [props.focusBeingEdited, props.validationError]);

	const formIsValid = useCallback(() => {
		const trimmedFocusName = focusName.trim();
		const focusNameIsValid = (trimmedFocusName.length > 0 && trimmedFocusName.length <= MAX_FOCUS_NAME_LENGTH) || !saveFocusToLibrary;
		const focusResourcesAreValid = selectedResources.length > 0 && selectedResources.every((resource) => resource.isValid);
		const allResourcesAreLockedFocusCompatible =
			selectedResources.length > 0 && selectedResources.every((resource) => resource.isLockedFocusCompatible);
		const meetsBasicValidation = focusResourcesAreValid && focusNameIsValid && (!lockedFocus || allResourcesAreLockedFocusCompatible);

		return meetsBasicValidation;
	}, [lockedFocus, selectedResources, focusName, saveFocusToLibrary, MAX_FOCUS_NAME_LENGTH]);

	const formHasChanged = useCallback(() => {
		if (!props.focusBeingEdited) return true;

		const existingResourceValues = props.focusBeingEdited.resources.map((resource) => resource.resourceValue);

		const allSelectedAlreadyExist = selectedResources.every((resource) => {
			return existingResourceValues.includes(resource.resourceValue);
		});
		const allExistingAreSelected = existingResourceValues.every((resource) => {
			return selectedResources.some((x) => x.resourceValue === resource);
		});
		const anySelectedAndExistingResourcesHaveDifferentOpenTab = selectedResources.some((resource) => {
			const existingResource = props.focusBeingEdited?.resources.find((x) => x.resourceValue === resource.resourceValue);
			return existingResource && existingResource.openTab !== resource.openTab;
		});

		const nameChanged = focusName !== props.focusBeingEdited.name;
		const resourcesChanged = !allSelectedAlreadyExist || !allExistingAreSelected || anySelectedAndExistingResourcesHaveDifferentOpenTab;
		const lockedFocusChanged =
			featureFlagsQuery.data?.includes(FeatureFlag.FOCUS_LOCK_MODE) &&
			selectedClassroomStore.stableIdV1 &&
			lockedFocus !== props.focusBeingEdited.tabsLocked;

		return nameChanged || resourcesChanged || lockedFocusChanged;
	}, [selectedResources, focusName, props.focusBeingEdited, lockedFocus, featureFlagsQuery.data, selectedClassroomStore.stableIdV1]);

	const [submitButtonsDisabled, setSubmitButtonsDisabled] = useState<boolean>(true);

	function handleChangeFocusName(event: ChangeEvent<HTMLInputElement>) {
		setFocusName(event.target.value);
	}

	function handleRemoveResource(resource: SelectedResource) {
		const newSelectedResources = selectedResources.filter((x) => x !== resource);
		setSelectedResources(newSelectedResources);
	}

	function handleToggleLockedFocus(e: ChangeEvent<HTMLInputElement>) {
		setLockedFocus(e.target.checked);
		if (e.target.checked) {
			const newSelectedResources = selectedResources.map((x) => ({ ...x, openTab: true }));
			setSelectedResources(newSelectedResources);
		}
	}

	// Enable/disable submit buttons based on form changes and validity
	useEffect(() => {
		// Submit button should be disabled if form is invalid or no changes have been made.
		// Exception: When editing an existing focus from the classroom page that is currently
		// active, we only need to check if the form is valid.
		if (!!selectedClassroomStore.stableIdV1 && props.focusBeingEdited) {
			if (props.openedFromAppliedFocusPill) {
				setSubmitButtonsDisabled(!formIsValid() || !formHasChanged());
				return;
			}

			setSubmitButtonsDisabled(!formIsValid());
			return;
		}

		setSubmitButtonsDisabled(!formIsValid() || !formHasChanged());
	}, [
		focusName,
		selectedResources,
		saveFocusToLibrary,
		formIsValid,
		formHasChanged,
		props.focusBeingEdited,
		props.openedFromAppliedFocusPill,
		selectedClassroomStore.stableIdV1,
	]);

	// Automatically assume we are saving to library if opened from My Classes page
	useEffect(() => {
		const focusIsBeingEdited = !!props.focusBeingEdited;
		const focusBeingEditedIsOnTheFly = !!props.focusBeingEdited && props.focusBeingEdited.isOnTheFly;

		setSaveFocusToLibrary(!selectedClassroomStore.stableIdV1 && (!focusIsBeingEdited || focusBeingEditedIsOnTheFly));
		if (props.focusBeingEdited && !props.focusBeingEdited.isOnTheFly) {
			setSaveFocusToLibrary(true);
			setFocusName(props.focusBeingEdited.name);
			setSelectedResources(props.focusBeingEdited.resources);
		}
	}, [props.focusBeingEdited, selectedClassroomStore.stableIdV1]);

	function handleCheckboxChanged(resource: SelectedResource) {
		const newSelectedResources = selectedResources.map((x) => (x === resource ? { ...x, openTab: !x.openTab } : x));
		setSelectedResources(newSelectedResources);
	}

	function handleAddResource(resource: SelectedResource) {
		// do not add duplicate resources
		if (selectedResources.some((x) => x.resourceValue === resource.resourceValue)) return;

		if (resource.resourceType === ResourceType.Url) {
			if (isLockedFocusCompatibleUrl(resource.resourceValue)) {
				resource.isLockedFocusCompatible = true;
			}
		}

		const newSelectedResources = [...selectedResources, resource];
		setSelectedResources(newSelectedResources);
	}

	function clearForm() {
		setFocusName("");

		setNameIsDuplicate(false);
		setSelectedResources([]);
	}

	function handleUpdateExistingFocusWithoutApplying(id: string): void {
		setIsUpserting(true);

		createOrUpdateFocus(
			transformSelectedResourcesToMatchAPIContract(selectedResources),
			!!featureFlagsQuery.data?.includes(FeatureFlag.FOCUS_LOCK_MODE) && lockedFocus,
			false,
			true,
			userSessionStore.getTeacherId() as string,
			focusName,
			undefined,
			undefined,
			id
		)
			.then((response) => {
				successToast({
					title: "Focus has been updated in your Focus Library.",
					description: "Start any class to use it.",
					isClosable: true,
				});

				clearForm();
				focusStore.upsertFocusItemInLibrary(transformUpsertResonseToFocusItem(response));

				props.closeDrawer();
			})
			.catch((e) => {
				console.error("Failed to save focus", e);
				props.handleUpsertFailure(e as Error);
			})
			.finally(() => {
				setIsUpserting(false);
			});
	}

	function handleCreateNewFocusWithoutApplying(createAnother: boolean) {
		setIsUpserting(true);

		createOrUpdateFocus(
			transformSelectedResourcesToMatchAPIContract(selectedResources),
			!!featureFlagsQuery.data?.includes(FeatureFlag.FOCUS_LOCK_MODE) && lockedFocus,
			false,
			true,
			userSessionStore.getTeacherId() as string,
			focusName
		)
			.then((response) => {
				successToast({
					title: "A new focus has been saved to your Focus Library.",
					description: "Start any class to use it.",
					isClosable: true,
				});

				clearForm();
				focusStore.upsertFocusItemInLibrary(transformUpsertResonseToFocusItem(response));

				if (!createAnother) {
					props.closeDrawer();
					return;
				}
			})
			.catch((e) => {
				console.error("Failed to save focus", e);
				props.handleUpsertFailure(e as Error);
			})
			.finally(() => {
				setIsUpserting(false);
			});
	}

	function handleApplyFocus(saveToLibrary: boolean): void {
		setIsUpserting(true);

		const studentIdsToBeAddedToFocus = props.focusBeingEdited?.studentIds || [];

		createOrUpdateFocus(
			transformSelectedResourcesToMatchAPIContract(selectedResources),
			!!featureFlagsQuery.data?.includes(FeatureFlag.FOCUS_LOCK_MODE) && lockedFocus,
			true,
			saveToLibrary,
			userSessionStore.getTeacherId() as string,
			saveToLibrary ? focusName.trim() : undefined,
			selectedClassroomStore.stableIdV1 as string,
			studentIdsToBeAddedToFocus,
			props.focusBeingEdited?.id
		)
			.then((response) => {
				successToast({
					title: `Focus has been ${saveToLibrary ? "updated and " : ""}applied to your class.`,
					isClosable: true,
				});

				clearForm();

				const focusItem = transformUpsertResonseToFocusItem(response);

				if (saveFocusToLibrary) focusStore.upsertFocusItemInLibrary(focusItem);

				if (props.bubbleAppliedOrUpdatedFocus) props.bubbleAppliedOrUpdatedFocus(focusItem);
				props.closeDrawer();
				//track focus event
				captureFocusEvent(
					!props.classIsActive,
					lockedFocus,
					userSessionStore.getActiveUser() as string,
					userSessionStore.getActiveDevice() as string
				);
			})
			.catch((e) => {
				console.error("Failed to save focus", e);
				props.handleUpsertFailure(e as Error);
			})
			.finally(() => {
				setIsUpserting(false);
			});
	}

	function handleConfirmDelete() {
		if (!props.focusBeingEdited) return;

		setIsDeleting(true);

		deleteSavedFocusFromLibrary(props.focusBeingEdited.id, userSessionStore.getTeacherId() as string)
			.then(() => {
				focusStore.removeFocusItemFromLibrary(props.focusBeingEdited?.id as string);
				successToast({ title: "The focus has been deleted from your Focus Library", isClosable: true });
				props.closeDrawer();
			})
			.catch((e) => {
				console.error("Failed to delete focus", e);
				errorToast({
					title: "Failed to delete the Focus.",
					description: UNEXPECTED_ERROR,
					isClosable: true,
				});
			})
			.finally(() => {
				setIsDeleting(false);
			});
	}

	function renderLeftFooterItem(): JSX.Element {
		if (!props.focusBeingEdited || props.focusBeingEdited.isOnTheFly) {
			return <></>;
		}

		if (props.focusBeingEdited?.active || (props.focusBeingEdited && !!selectedClassroomStore.stableIdV1)) {
			return <Text>Updates will be saved to your library</Text>;
		}

		return (
			<Button data-testid="delete-focus-button" variant={"danger"} onClick={() => confirmationModal.onOpen()}>
				<ChakraFAIcon icon={"fa-trash-can"} variant="solid" mr={2} boxSize={4} />
				Delete
			</Button>
		);
	}

	function handleClickSelectStudentsButton() {
		const focusToBeApplied: EditingFocus = {
			id: props.focusBeingEdited?.id as string, // Will be undefined when creating a new focus.
			name: focusName,
			active: false,
			resources: selectedResources,
			tabsLocked: lockedFocus,
			isOnTheFly: !saveFocusToLibrary,
			studentIds: [],
		};

		props.setEditingFocus(focusToBeApplied);
		props.jumpToView(LibraryDrawerView.SelectStudents);
	}

	function handleClickApplyUpdateFocusButton() {
		if (props.focusBeingEdited && props.focusBeingEdited?.isOnTheFly) {
			handleApplyFocus(false);
			return;
		}

		if (props.focusBeingEdited && !selectedClassroomStore.stableIdV1) {
			handleUpdateExistingFocusWithoutApplying(props.focusBeingEdited?.id);
			return;
		}

		if (selectedClassroomStore.stableIdV1) {
			handleApplyFocus(saveFocusToLibrary);
			return;
		}
	}

	function renderRightFooterItem(): JSX.Element {
		if (!selectedClassroomStore.stableIdV1 && !props.focusBeingEdited) {
			return (
				<>
					<Tooltip
						label={
							focusStore.getNumberOfFocusesInLibrary() >= MAX_FOCUSES_IN_LIBRARY - 1
								? `You cannot save more than ${MAX_FOCUSES_IN_LIBRARY} focus plans`
								: "Save focus to your library and create another focus"
						}
					>
						<Button
							data-testid="save-and-create-new-focus-button"
							variant={"outline"}
							mr={15}
							ml={"auto"}
							isDisabled={submitButtonsDisabled || focusStore.getNumberOfFocusesInLibrary() >= MAX_FOCUSES_IN_LIBRARY - 1}
							onClick={() => handleCreateNewFocusWithoutApplying(true)}
						>
							<ChakraFAIcon icon={"fa-plus"} variant="solid" mr={3} boxSize={4} />
							Save & Create New
						</Button>
					</Tooltip>
					<Tooltip
						label={
							focusStore.getNumberOfFocusesInLibrary() >= MAX_FOCUSES_IN_LIBRARY
								? `You cannot save more than ${MAX_FOCUSES_IN_LIBRARY} focus plans`
								: "Save Focus to your library"
						}
					>
						<Button
							data-testid="save-focus-button"
							isDisabled={submitButtonsDisabled || focusStore.getNumberOfFocusesInLibrary() >= MAX_FOCUSES_IN_LIBRARY}
							onClick={() => handleCreateNewFocusWithoutApplying(false)}
						>
							<ChakraFAIcon icon={"fa-floppy-disk"} variant="solid" mr={2} boxSize={4} />
							Save Focus
						</Button>
					</Tooltip>
				</>
			);
		}

		function getActionButtonLabel(): string {
			if (selectedClassroomStore.stableIdV1) {
				if (props.openedFromAppliedFocusPill) return "Update Focus";

				if (props.classIsActive) return "Apply Focus";
				return "Preset Focus";
			} else {
				if (props.focusBeingEdited) return "Update Focus";
				return "Save Focus";
			}
		}

		const icon = !selectedClassroomStore.stableIdV1 ? "fa-floppy-disk" : "fa-arrows-to-eye";

		const showSelectStudentsButton = !!selectedClassroomStore.stableIdV1 && !props.openedFromAppliedFocusPill;

		if (showSelectStudentsButton) {
			return (
				<Button data-testid="select-students-to-focus-button" isDisabled={submitButtonsDisabled} onClick={handleClickSelectStudentsButton}>
					Select Students
					<ChakraFAIcon icon={"fa-arrow-right"} variant="solid" ml={2} boxSize={4} />
				</Button>
			);
		} else {
			return (
				<Button
					data-testid="apply-or-update-focus-button"
					isDisabled={submitButtonsDisabled}
					onClick={handleClickApplyUpdateFocusButton}
					isLoading={isUpserting}
				>
					<ChakraFAIcon icon={icon} variant="solid" mr={2} boxSize={4} />
					{getActionButtonLabel()}
				</Button>
			);
		}
	}

	function handleBackButtonClicked() {
		props.jumpToView(LibraryDrawerView.LibraryView);
	}

	const drawerHeading = props.focusBeingEdited
		? props.focusBeingEdited.name && !!selectedClassroomStore.stableIdV1 && !focusStore.openedDirectlyToEdit
			? props.focusBeingEdited.name
			: "Edit Focus"
		: "Create Focus";
	const showBackButton =
		(!!selectedClassroomStore.stableIdV1 && !focusStore.openedDirectlyToEdit) ||
		(!selectedClassroomStore.stableIdV1 && props.focusBeingEdited);

	const editingExistingSavedFocus = !!props.focusBeingEdited && !props.focusBeingEdited.isOnTheFly;
	const showSaveToLibraryCheckbox = !!selectedClassroomStore.stableIdV1 && !props.focusBeingEdited; // Only need to show the checkbox when CREATING a focus from the Classroom page
	const showNameField =
		!selectedClassroomStore.stableIdV1 || // If creating or editing a focus from the My Classes page it will always be saved to the library
		editingExistingSavedFocus || // If the focus is already saved to the library, we need to be able to edit the name
		(showSaveToLibraryCheckbox && saveFocusToLibrary); // If we are creating a new focus from the Classroom page and saving it to the library we need to choose a name.

	return (
		<>
			<Flex bg={colours.gray[100]} px={7} py={7} justifyContent={"space-between"} alignItems={"center"}>
				<Flex alignItems={"center"}>
					{showBackButton && (
						<ChakraFAIcon
							data-testid="go-back-button"
							icon={"fa-arrow-left"}
							variant="solid"
							mr={25}
							boxSize={5}
							cursor={"pointer"}
							onClick={handleBackButtonClicked}
						/>
					)}
					<Text data-testid="drawer-heading" fontSize={"xl"} fontWeight={"medium"}>
						{drawerHeading}
					</Text>
				</Flex>
			</Flex>

			<DrawerBody pt={3}>
				{showSaveToLibraryCheckbox && (
					<Tooltip
						label={`You cannot save more than ${MAX_FOCUSES_IN_LIBRARY} focus plans`}
						isDisabled={focusStore.focusLibrary.length < MAX_FOCUSES_IN_LIBRARY}
					>
						<FormControl mb="24px" isDisabled={focusStore.focusLibrary.length >= MAX_FOCUSES_IN_LIBRARY}>
							<Checkbox
								data-testid="save-to-library-checkbox"
								isChecked={saveFocusToLibrary}
								onChange={() => setSaveFocusToLibrary(!saveFocusToLibrary)}
							>
								Save this focus to my library
							</Checkbox>
						</FormControl>
					</Tooltip>
				)}

				{showNameField && (
					<FormControl mb="24px" isInvalid={focusName.length > MAX_FOCUS_NAME_LENGTH || nameIsDuplicate} isRequired>
						<Flex justifyContent={"space-between"}>
							<FormLabel fontWeight="medium" mb={1}>
								Name
							</FormLabel>
							<Text fontSize={"sm"} color={"neutrals.300"}>
								Max {MAX_FOCUS_NAME_LENGTH} characters are allowed.
							</Text>
						</Flex>
						<Input
							data-testid="focus-name-input"
							placeholder={"Give this focus a name"}
							value={focusName}
							onChange={handleChangeFocusName}
						/>
						<FormErrorMessage fontWeight="regular" mt="4px">
							{nameIsDuplicate && "Focus name already exists. Please use a different name"}
							{nameIsDuplicate && focusName.length > MAX_FOCUS_NAME_LENGTH && <br />}
							{focusName.length > MAX_FOCUS_NAME_LENGTH && `Focus name must have maximum ${MAX_FOCUS_NAME_LENGTH} characters`}
						</FormErrorMessage>
					</FormControl>
				)}

				{openedFromClassroomPage && !!featureFlagsQuery.data?.includes(FeatureFlag.FOCUS_LOCK_MODE) && (
					<>
						<FormControl mb={15} display={"flex"} alignItems={"center"} gap={3}>
							<Switch
								data-testid="locked-focus-switch"
								id="locked-focus-switch"
								isChecked={lockedFocus}
								onChange={handleToggleLockedFocus}
							/>
							<FormLabel htmlFor="locked-focus-switch" mt={2} fontWeight={"regular"} cursor={"pointer"}>
								Locked Focus
							</FormLabel>
						</FormControl>

						{!lockedFocus && (
							<Notice status="info" mb={15}>
								<Text fontWeight={500} mb={2}>
									Turn On Locked Focus
								</Text>
								<Text>
									Locked Focus adds extra browsing restrictions to students on Chromebooks and some Windows devices.{" "}
									<Link
										href="https://help.classwize.net/hc/en-gb/articles/21065900439708-Will-Locked-Focus-work-on-my-students"
										target="_blank"
										color={"inherit"}
										textDecoration={"underline"}
									>
										Find out more
									</Link>
								</Text>
							</Notice>
						)}

						{lockedFocus && (
							<Notice status="info" mb={15}>
								<Text fontWeight={500} mb={2}>
									How to use Locked Focus
								</Text>
								<Text>
									Students will be locked to all Focus tabs. Locked Focus only supports full website address, including "http://" or
									"https://", and the complete path.
									<br />
									For example, "https://en.wikipedia.org/wiki/Gold" will work, but "www.wikipedia.org" will not.
								</Text>
							</Notice>
						)}
					</>
				)}

				<FocusResourcePickerInput addResource={handleAddResource} lockedFocus={lockedFocus} />

				<SelectedFocusResources
					selectedResources={selectedResources}
					onRemove={handleRemoveResource}
					onCheckboxChanged={handleCheckboxChanged}
					lockedFocus={lockedFocus}
				/>
			</DrawerBody>
			<DrawerFooter bg={colours.gray[100]} display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
				<Box>{renderLeftFooterItem()}</Box>
				<Box>{renderRightFooterItem()}</Box>
			</DrawerFooter>
			{props.focusBeingEdited && (
				<ConfirmDeleteFocusModal
					name={props.focusBeingEdited.name}
					isDeleting={isDeleting}
					isOpen={confirmationModal.isOpen}
					closeModal={confirmationModal.onClose}
					handleConfirmDelete={handleConfirmDelete}
				/>
			)}
		</>
	);
}
